import type { EgressApi, SubscribeResult } from "./egress.js";
import type { CallsPublishedTrack } from "./tracks.js";

/** Minimal client surface the egress pipeline needs (satisfied by `@riq/api`'s ApiClient). */
export interface EgressApiClient {
  subscribeToBroadcast(): Promise<Record<string, unknown>>;
  renegotiateSession(sessionId: string, sdpAnswer: string): Promise<Record<string, unknown>>;
}

type SessionDescription = { sdp: string; type: RTCSdpType };

function toSessionDescription(value: unknown): SessionDescription | undefined {
  const desc = value as SessionDescription | undefined;
  return desc ? { sdp: desc.sdp, type: desc.type } : undefined;
}

/**
 * Adapt an `@riq/api` client to the typed {@link EgressApi}, centralizing the
 * `Record<string, unknown>` → typed-response casts that the listener flow needs.
 */
export function createEgressApi(client: EgressApiClient): EgressApi {
  return {
    async subscribe() {
      const data = await client.subscribeToBroadcast();
      const result: SubscribeResult = {
        sessionId: data.sessionId as string,
        requiresImmediateRenegotiation: data.requiresImmediateRenegotiation as boolean | undefined,
        sessionDescription: toSessionDescription(data.sessionDescription),
        tracks: data.tracks as CallsPublishedTrack[] | undefined,
      };
      return result;
    },
    renegotiateSession(sessionId, sdpAnswer) {
      return client.renegotiateSession(sessionId, sdpAnswer);
    },
  };
}
